/**
 * FAQ — investor-facing frequently asked questions + contact CTA.
 *
 * Loads FAQs from existing /documents?category=faq endpoint. If the backend
 * returns nothing, falls back to a small built-in static list so the section
 * is never empty. The bottom CTA reads WhatsApp contact URL from:
 *   - REACT_APP_WHATSAPP_CONTACT_URL
 * (The Calendly "Agendar llamada" CTA was retired from the investor view;
 * the env var / global integration can stay defined for other surfaces.)
 * Missing env vars → button renders disabled with a tooltip explaining why.
 */
import React, { useEffect, useState } from "react";
import { Section } from "@/pages/Shell";
import { api } from "@/lib/api";
import useInvestorLanguage from "@/i18n/useInvestorLanguage";
import { CaretDown, CaretRight, Phone, WhatsappLogo } from "@phosphor-icons/react";

const TERRACOTTA = "#9c5236";

const FALLBACK_FAQS = [
  {
    title: "¿Cómo se estructuran los pagos de mi villa?",
    body: "Los pagos están escalonados en 4 fases: reserva inicial, cimentación, estructura y entrega. Cada hito desbloquea el siguiente tramo de la inversión.",
  },
  {
    title: "¿Puedo seguir el avance de la obra en tiempo real?",
    body: "Sí. En la sección 'Obra en vivo' encontrarás fotos y actualizaciones del equipo de construcción. También recibirás notificaciones cuando se complete una fase.",
  },
  {
    title: "¿Cuándo recibiré la entrega de mi villa?",
    body: "La fecha estimada de entrega aparece en tu sección 'Mi proyecto'. Te avisaremos de cualquier cambio significativo en el cronograma con anticipación.",
  },
  {
    title: "¿Cómo se calcula el rendimiento esperado?",
    body: "Puedes usar el simulador en la sección 'Rendimiento' para proyectar ingresos según ocupación, tarifa media por noche y costes de gestión. La proyección oficial al firmar aparece en 'Mi proyecto'.",
  },
  {
    title: "¿Dónde encuentro mis documentos legales?",
    body: "Todos los contratos, recibos y documentación legal están disponibles en la sección 'Documentos', organizados por categoría.",
  },
];

function FaqItem({ title, body, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-stone-200 dark:border-stone-700 rounded-md bg-white dark:bg-stone-900" data-testid="faq-item">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-start gap-3 text-left p-4 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
        aria-expanded={open}
      >
        <span className="mt-0.5 shrink-0 text-stone-400 dark:text-stone-500">
          {open ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
        </span>
        <span className="font-medium text-stone-800 dark:text-stone-100">{title}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pl-11 text-sm text-stone-700 dark:text-stone-300 leading-relaxed whitespace-pre-wrap">
          {body}
        </div>
      )}
    </div>
  );
}

function ContactCTA({ t }) {
  const whatsappUrl  = (process.env.REACT_APP_WHATSAPP_CONTACT_URL || "").trim();

  const Btn = ({ href, label, Icon, testid }) => {
    const enabled = !!href;
    const className = `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium border transition-colors ${
      enabled
        ? "border-transparent text-white hover:opacity-90"
        : "border-stone-200 dark:border-stone-700 text-stone-400 dark:text-stone-500 bg-white dark:bg-stone-800 cursor-not-allowed"
    }`;
    const style = enabled ? { background: TERRACOTTA } : undefined;
    if (!enabled) {
      return (
        <span
          className={className}
          style={style}
          title={t("faq.cta.disabled")}
          data-testid={testid}
          aria-disabled="true"
        >
          <Icon size={16} weight="bold" /> {label}
        </span>
      );
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        style={style}
        data-testid={testid}
      >
        <Icon size={16} weight="bold" /> {label}
      </a>
    );
  };

  return (
    <div
      className="rounded-lg p-6 sm:p-8 mt-8 border bg-[#fbf3ee] dark:bg-stone-900 border-[#e4cdbf] dark:border-stone-700"
      data-testid="faq-contact-cta"
    >
      <div className="text-center max-w-xl mx-auto">
        <Phone size={28} weight="light" className="mx-auto mb-2" style={{ color: TERRACOTTA }} />
        <div className="font-serif-dv text-2xl sm:text-3xl mb-2 text-stone-900 dark:text-stone-50">
          {t("faq.cta.title")}
        </div>
        <div className="text-sm text-stone-600 dark:text-stone-300 mb-5">
          {t("faq.cta.subtitle")}
        </div>
        <div className="flex flex-wrap gap-3 justify-center">
          <Btn
            href={whatsappUrl}
            label={t("faq.cta.whatsapp")}
            Icon={WhatsappLogo}
            testid="faq-cta-whatsapp"
          />
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const { t } = useInvestorLanguage();
  const [docs, setDocs] = useState(null); // null = loading, [] = empty, [...] = list

  useEffect(() => {
    api.get("/documents", { params: { category: "faq" } })
      .then(r => setDocs(Array.isArray(r.data) ? r.data : []))
      .catch(() => { setDocs([]); });
  }, []);

  const usingFallback = docs && docs.length === 0;
  // Frontend hide list — legal / regulatory FAQ blocks the team decided to
  // retire from the investor view. The underlying document is left intact
  // on the backend; only this surface stops rendering it.
  const HIDDEN_FAQ_TITLE_RE = /(hak\s*sewa|repatriaci)/i;
  const items = (docs && docs.length > 0)
    ? docs
        .filter(d => !HIDDEN_FAQ_TITLE_RE.test(d.title || ""))
        .map(d => ({ title: d.title, body: d.description || d.title }))
    : FALLBACK_FAQS;

  return (
    <Section title={t("faq.title")} subtitle={t("faq.subtitle")}>
      {docs === null ? (
        <div className="dv-card text-center py-12 text-stone-500 dark:text-stone-400">{t("faq.loading")}</div>
      ) : (
        <>
          {usingFallback && (
            <div className="mb-4 text-xs text-stone-500 dark:text-stone-400 italic">
              {t("faq.fallback_hint")}
            </div>
          )}
          <div className="space-y-2">
            {items.map((it, i) => (
              <FaqItem key={i} title={it.title} body={it.body} defaultOpen={i === 0} />
            ))}
          </div>
          <ContactCTA t={t} />
        </>
      )}
    </Section>
  );
}
